/**
 * The members panel of a channel (from the header's member pill): who is in it (online first, then by name) with
 * presence, role, external organization and sponsor; search; "Add people" (my directory, `can.invite`); Remove
 * (admins/owners or the creator, never in #general). The list comes from the server (online) and is refetched when
 * the channel's member count changes (member_joined / member_left elsewhere).
 */
import { msg, type MessageDescriptor } from "../i18n.ts";
import {
  addableMembers,
  channelMemberRows,
  channelMembersPermissions,
  type ChannelMemberView,
  type MemberOption,
} from "../logic/channels.ts";
import { channelTitle } from "../logic/sidebar.ts";
import { ViewModel } from "./ViewModel.ts";
import type { WorkspaceViewModel } from "./WorkspaceViewModel.ts";

const PICKER_LIMIT = 8;

export type ChannelMembersState = {
  channelId: string;
  title: string;
  /** "loading" until the first list; "offline" when it can't be fetched without a connection. */
  status: "loading" | "ready" | "offline" | "error";
  /** Everyone in the channel (the header's count). */
  count: number;
  rows: ChannelMemberView[];
  query: string;
  /** #general: everyone is in it. */
  isDefault: boolean;
  canAdd: boolean;
  canRemove: boolean;
  /** "Add people" is open. */
  adding: boolean;
  addQuery: string;
  /** People I can add (not in the channel, not picked yet). */
  candidates: MemberOption[];
  picked: MemberOption[];
  busy: boolean;
  notice?: MessageDescriptor;
};

export class ChannelMembersViewModel extends ViewModel<ChannelMembersState> {
  private readonly ws: WorkspaceViewModel;
  private readonly channelId: string;
  private memberIds: string[] = [];
  /** The cached member count the list was fetched for (refetch when it changes). */
  private fetchedCount = -1;
  private loading = false;

  constructor(ws: WorkspaceViewModel, channelId: string) {
    super({
      channelId,
      title: "",
      status: "loading",
      count: 0,
      rows: [],
      query: "",
      isDefault: false,
      canAdd: false,
      canRemove: false,
      adding: false,
      addQuery: "",
      candidates: [],
      picked: [],
      busy: false,
    });
    this.ws = ws;
    this.channelId = channelId;
    this.onDispose(ws.subscribe(() => this.derive({})));
    this.derive({});
  }

  private derive(patch: Partial<ChannelMembersState>): void {
    const s = { ...this.state, ...patch };
    const cache = this.ws.state.cache;
    if (cache === undefined) {
      this.setState(s);
      return;
    }
    const now = this.ws.deps.clock.now();
    const perms = channelMembersPermissions(cache, this.channelId, s.isDefault, now);
    const count = cache.channels[this.channelId]?.memberCount ?? this.memberIds.length;
    this.setState({
      ...s,
      title: channelTitle(cache, this.channelId),
      count,
      rows: s.status === "ready" ? channelMemberRows(cache, this.ws.state.live, this.channelId, this.memberIds, s.query, s.isDefault, now) : [],
      canAdd: perms.canAdd,
      canRemove: perms.canRemove,
      candidates: s.adding ? addableMembers(cache, s.addQuery, [...this.memberIds, ...s.picked.map((p) => p.userId)], now, PICKER_LIMIT) : [],
    });
    // Someone joined or left elsewhere: the count moved under the list.
    if (s.status === "ready" && this.fetchedCount !== -1 && count !== this.fetchedCount && !this.loading) void this.load();
  }

  /** Fetches the list (each time the panel opens, and when the member count changes). */
  async load(): Promise<void> {
    if (this.loading) return;
    if (!this.ws.state.network) {
      if (this.state.status !== "ready") this.derive({ status: "offline" });
      return;
    }
    this.loading = true;
    try {
      const res = await this.ws.deps.data.channelMembers(this.ws.state.wsId, this.channelId);
      if (this.isDisposed) return;
      this.memberIds = res.members.map((m) => m.userId);
      this.fetchedCount = this.ws.state.cache?.channels[this.channelId]?.memberCount ?? this.memberIds.length;
      this.loading = false;
      this.derive({ status: "ready", isDefault: res.isDefault });
    } catch {
      this.loading = false;
      if (!this.isDisposed && this.state.status !== "ready") this.derive({ status: this.ws.state.network ? "error" : "offline" });
    }
  }

  setQuery(query: string): void {
    this.derive({ query });
  }

  startAdding(): void {
    if (this.state.canAdd) this.derive({ adding: true, addQuery: "", picked: [] });
  }

  cancelAdding(): void {
    this.derive({ adding: false, addQuery: "", picked: [] });
  }

  setAddQuery(addQuery: string): void {
    this.derive({ addQuery });
  }

  pick(userId: string): void {
    const p = this.state.candidates.find((x) => x.userId === userId);
    if (p !== undefined) this.derive({ picked: [...this.state.picked, p], addQuery: "" });
  }

  unpick(userId: string): void {
    this.derive({ picked: this.state.picked.filter((x) => x.userId !== userId) });
  }

  /** Adds the picked people. */
  async addPicked(): Promise<void> {
    const ids = this.state.picked.map((p) => p.userId);
    if (ids.length === 0 || this.state.busy) return;
    this.derive({ busy: true });
    try {
      await this.ws.deps.data.addChannelMembers(this.ws.state.wsId, this.channelId, ids);
      if (this.isDisposed) return;
      this.memberIds = [...this.memberIds, ...ids.filter((id) => !this.memberIds.includes(id))];
      this.derive({ busy: false, adding: false, picked: [], addQuery: "" });
      void this.load();
    } catch {
      if (!this.isDisposed) this.derive({ busy: false, notice: this.ws.state.network ? msg("channelMembers.addFailed") : msg("channelMembers.offline") });
    }
  }

  /** Removes someone (shown gone at once; back with a notice if the server refuses). */
  async remove(userId: string): Promise<void> {
    const row = this.state.rows.find((r) => r.userId === userId);
    if (row === undefined || !row.canRemove) return;
    const before = this.memberIds;
    this.memberIds = before.filter((id) => id !== userId);
    this.derive({});
    try {
      await this.ws.deps.data.removeChannelMember(this.ws.state.wsId, this.channelId, userId);
    } catch {
      if (this.isDisposed) return;
      this.memberIds = before;
      this.derive({ notice: msg("channelMembers.removeFailed", { name: row.name }) });
    }
  }

  dismissNotice(): void {
    this.derive({ notice: undefined });
  }
}
